import React, { useState, useEffect } from 'react';
import {
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  RateReviewOutlined as RateReviewOutlinedIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';
import "./styles.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from '../../../utilities/LoadSpinner';
import { postRightworkAPI } from '../requests/utilities/requests';
import {
  API_post_learning_manager_input_completion,
} from '../../../constants';

export const ManagerInput = (props) => {
  const { block, editor } = props;

  // Config from block props - use managerInputBlockData if available, otherwise direct props
  const blockData = block.props.managerInputBlockData || {};
  const [config, setConfig] = useState({
    title: blockData.title || block.props.title || "Manager Input Required",
    description: blockData.description || block.props.description || "",
    required: blockData.required !== undefined ? blockData.required : (block.props.required !== undefined ? block.props.required : true),
    save_to_profile: blockData.save_to_profile !== undefined ? blockData.save_to_profile : (block.props.save_to_profile !== undefined ? block.props.save_to_profile : false),
  });

  // State for completion data
  const [completionData, setCompletionData] = useState(null);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(false);

  // State for manager input
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if input has been completed
  const isCompleted = Boolean(completionData?.is_completed);

  // Open by default in edit mode so it's obvious the block is configurable
  // (only rendered there; complete mode never shows the panel).
  const [showSettings, setShowSettings] = useState(true);

  const updateConfig = (newConfig) => {
    setConfig(newConfig);
    // Update the block props in the editor
    editor.updateBlock(block, {
      type: "managerInput",
      props: {
        ...block.props,
        ...newConfig,
      },
    });
  };

  // Load completion status from block props
  useEffect(() => {
    const loadCompletionStatus = async () => {
      const blockData = block.props.managerInputBlockData;

      if (props.mode === "complete" && !props.isPreview && blockData) {
        setIsLoadingCompletion(true);
        try {
          // Completion status is included in the block data from the backend
          const isCompleted = blockData.user_has_completed === true;

          setCompletionData({
            is_completed: isCompleted,
            input_value: blockData.input_value || "",
            manager_name: blockData.manager_name || null,
            completed_at: blockData.completed_at || null,
          });

          if (isCompleted && blockData.input_value) {
            setInputValue(blockData.input_value);
          }
        } catch (error) {
          console.error('Error loading manager input completion status:', error);
        } finally {
          setIsLoadingCompletion(false);
        }
      }
    };

    loadCompletionStatus();
  }, [props.mode, props.isPreview, block.props.managerInputBlockData]);

  // Handle manager submitting input
  const handleSubmitInput = async () => {
    if (!inputValue.trim()) {
      toast.error('Please enter a value before submitting.', { autoClose: 3000 });
      return;
    }

    try {
      const managerInputBlockUUID = block.props.managerInputBlockData?.uuid;
      if (!managerInputBlockUUID) {
        console.error('No manager input block UUID found');
        toast.error('Configuration error. Please contact your administrator.', { autoClose: 3000 });
        return;
      }

      setIsSubmitting(true);

      const params = {
        manager_input_block_uuid: managerInputBlockUUID,
        section_uuid: props.sectionUUID,
        input_value: inputValue,
        company_id: props.homeCompany,
        store_team_member_id: props.employeeID,
      };

      const response = await postRightworkAPI(API_post_learning_manager_input_completion, params);

      const payload = response?.data?.redux?.payload;

      // Update local completion state
      setCompletionData({
        is_completed: true,
        input_value: inputValue,
        manager_name: payload?.manager_input_completion?.manager_name,
        completed_at: new Date().toISOString(),
      });
      // No success popup — the block re-renders into its completed (emerald)
      // state, mirroring how the Form block confirms a submission inline.
    } catch (error) {
      console.error('Error saving manager input:', error);
      toast.error('Failed to save input. Please try again.', { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTranslation = (key) => {
    const translations = {
      managerInputRequired: {
        en: 'Manager Input Required',
        es: 'Se Requiere Entrada del Gerente'
      },
      enterScore: {
        en: 'Enter Score/Value',
        es: 'Ingresar Puntuación/Valor'
      },
      submitInput: {
        en: 'Submit Input',
        es: 'Enviar Entrada'
      },
      required: {
        en: 'Required',
        es: 'Requerido'
      },
      completed: {
        en: 'Completed',
        es: 'Completado'
      },
      inputProvided: {
        en: 'Input Provided',
        es: 'Entrada Proporcionada'
      }
    };

    return translations[key]?.[props.language] || translations[key]?.en || key;
  };

  // Complete/Preview mode - Shows the actual interface
  if (props.mode === "complete") {
    // If this is a preview (manager viewing in track/module preview), show static view
    const isPreview = props.isPreview === true;

    // Determine if current user is a manager
    const isManager = props.userType !== "Employee";

    if (isPreview) {
      return (
        <div className="w-full bg-white rounded-lg border border-gray-200 p-4" contentEditable={false}>
          <div className="flex items-start gap-3">
            <RateReviewOutlinedIcon className="text-gray-400 mt-1" style={{ fontSize: 32 }} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>
                  {config.title}
                </span>
                {config.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 font-medium rounded" style={{ fontSize: '12px' }}>
                    Required
                  </span>
                )}
              </div>

              {config.description && (
                <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                  {config.description}
                </p>
              )}

              {/* Mock input interface */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-gray-500 mb-2" style={{ fontSize: '12px' }}>
                  (Manager input interface - preview only)
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Enter value..."
                    disabled
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-400 text-white rounded-lg text-sm font-medium cursor-not-allowed"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Active view (employee or manager completing training)
    return (
      <div
        className={`w-full border rounded-lg transition-all ${
          isCompleted
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-slate-200 bg-white'
        }`}
        contentEditable={false}
      >
        {isLoadingCompletion ? (
          <div className="px-4 py-6 flex items-center justify-center">
            <LoadSpinner size={24} />
          </div>
        ) : (
          <div className="px-4 py-4">
            <div className="flex items-start gap-3">
              {isCompleted ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
                </div>
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                  <RateReviewOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>
                    {config.title}
                  </span>
                  {config.required && !isCompleted && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 font-medium rounded" style={{ fontSize: '12px' }}>
                      {getTranslation('required')}
                    </span>
                  )}
                  {isCompleted && (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 font-medium rounded" style={{ fontSize: '12px' }}>
                      {getTranslation('completed')}
                    </span>
                  )}
                </div>

                {config.description && (
                  <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                    {config.description}
                  </p>
                )}

                <div className={`mt-3 pt-3 border-t ${isCompleted ? "border-emerald-100" : "border-gray-100"}`}>
                  {isCompleted ? (
                    <>
                      <p className="text-emerald-600 mb-2 font-medium" style={{ fontSize: 12 }}>
                        {getTranslation('completed')} {completionData?.completed_at && `on ${new Date(completionData.completed_at).toLocaleDateString()}`}
                      </p>
                      <div className="bg-white rounded-lg border border-emerald-200 p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-500 mb-1" style={{ fontSize: '11px' }}>{getTranslation('inputProvided')}:</p>
                            <p className="text-lg font-semibold text-gray-900">{completionData?.input_value}</p>
                          </div>
                          {completionData?.manager_name && (
                            <p className="text-gray-500" style={{ fontSize: '12px' }}>
                              by {completionData.manager_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : isManager ? (
                    <div>
                      <label className="block font-medium text-gray-700 mb-1.5" style={{ fontSize: '12px' }}>
                        {getTranslation('enterScore')}
                      </label>
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          type="text"
                          placeholder="e.g., 95, Pass, 4.5/5"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent"
                        />
                        <button
                          onClick={handleSubmitInput}
                          disabled={isSubmitting}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSubmitting ? <LoadSpinner size={16} /> : <EditNoteIcon style={{ fontSize: 18 }} />}
                          {getTranslation('submitInput')}
                        </button>
                      </div>
                      <p className="text-gray-500" style={{ fontSize: '11px' }}>
                        Enter the score, grade, or completion status for this employee
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-yellow-800" style={{ fontSize: '14px' }}>
                        {props.language === "spanish"
                          ? "Esperando la entrada del gerente. Tu gerente debe completar esto antes de que puedas continuar."
                          : "Waiting for manager input. Your manager must complete this before you can continue."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Edit mode - Shows configuration interface (manager side)
  return (
    <div
      className="w-full border-2 border-gray-200 rounded-lg bg-gray-50 outline-none focus:outline-none"
      contentEditable={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <RateReviewOutlinedIcon className="text-violet-700" style={{ fontSize: 24 }} />
          <div>
            <div className="font-semibold text-gray-700" style={{ fontSize: '14px' }}>
              Manager Input Block
            </div>
            <div className="text-gray-500" style={{ fontSize: '12px' }}>
              Managers can input values/scores for employees
            </div>
          </div>
        </div>

        {/* Settings Toggle Button */}
        <button
          className="cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors flex items-center gap-1"
          onClick={() => setShowSettings(!showSettings)}
          type="button"
        >
          <SettingsIcon className="text-violet-700" style={{ fontSize: 20 }} />
          {showSettings ? (
            <ExpandLessIcon className="text-violet-700" style={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon className="text-violet-700" style={{ fontSize: 18 }} />
          )}
        </button>
      </div>

      {/* Custom Settings Panel */}
      {showSettings && (
        <div className="px-4 py-4 bg-white border-b border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4" style={{ fontSize: '14px' }}>Configure Manager Input</h4>

          {/* Title Input */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-1.5 mt-1" style={{ fontSize: '12px' }}>
              Title
            </label>
            <input
              type="text"
              placeholder="e.g., Offline Test Score, Performance Rating"
              value={config.title}
              onChange={(e) => updateConfig({ ...config, title: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent"
            />
          </div>

          {/* Description Textarea */}
          <div className="mb-4">
            <label className="block font-medium text-gray-700 mb-1.5" style={{ fontSize: '12px' }}>
              Instructions
            </label>
            <textarea
              placeholder="Describe what the manager should input (e.g., score from practical test, grade from evaluation)..."
              value={config.description}
              onChange={(e) => updateConfig({ ...config, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent resize-none"
            />
          </div>

          <div className="border-t border-gray-200 my-4"></div>

          {/* Required Switch */}
          <div className="mb-0">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="font-medium text-gray-900" style={{ fontSize: '14px' }}>Require to complete training</div>
                <div className="text-gray-500" style={{ fontSize: '12px' }}>Employees cannot proceed without manager input</div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.required}
                  onChange={(e) => updateConfig({ ...config, required: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div>

          <div className="border-t border-gray-200 my-4"></div>

          {/* Save to employee profile switch */}
          <div className="mb-0">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="font-medium text-gray-900" style={{ fontSize: '14px' }}>Keep a copy on the employee's profile</div>
                <div className="text-gray-500" style={{ fontSize: '12px' }}>Stays on their profile even after the training changes or is unassigned</div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.save_to_profile}
                  onChange={(e) => updateConfig({ ...config, save_to_profile: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Preview of what employees/managers will see */}
      <div className="px-4 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <RateReviewOutlinedIcon className="text-gray-400 mt-1" style={{ fontSize: 32 }} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>
                  {config.title}
                </span>
                {config.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 font-medium rounded" style={{ fontSize: '12px' }}>
                    Required
                  </span>
                )}
              </div>

              {config.description && (
                <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                  {config.description}
                </p>
              )}

              {/* Mock input button */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Manager enters value here..."
                    disabled
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-400 text-white rounded-lg text-sm font-medium cursor-not-allowed"
                  >
                    Submit
                  </button>
                </div>
                <p className="text-gray-500" style={{ fontSize: '12px' }}>
                  (Manager input interface - preview only)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer help text */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center gap-1.5 text-gray-600" style={{ fontSize: '12px' }}>
          <TipsAndUpdatesIcon style={{ fontSize: 16 }} />
          <span>Click the settings icon to configure this manager input block</span>
        </div>
      </div>
    </div>
  );
};
